import ServiceRecordAttribution from '../ServiceRecordAttribution';
import ServiceRecordChangeLog from '../ServiceRecordChangeLog';

import { HorizontalContainer, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import PanelSection from 'components/PanelSection';

import ServicePhotoThumb from 'pages/MaintenancePage/components/LogServicePanel/components/ServicePhotosField/components/ServicePhotoThumb';
import { getCanEditRecord } from 'pages/MaintenancePage/helpers/getCanEditRecord';
import { getServiceRecordSummary } from 'pages/MaintenancePage/helpers/getServiceRecordSummary';
import useTranslation from 'pages/MaintenancePage/hooks/useTranslation';

import {
    BACK_TEST_ID,
    DETAIL_ROW_TEST_ID,
    EDIT_TEST_ID,
    PHOTOS_TEST_ID,
    TEST_ID
} from './constants';
import { getDetailRows } from './helpers/getDetailRows';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const ServiceRecordDetail: FunctionComponent<Props> = ({
    className = '',
    isAdmin,
    onBack,
    onEditRecord,
    record,
    serviceIntervals,
    testId = TEST_ID
}) => {
    const t = useTranslation();
    const summary = getServiceRecordSummary({ record, strings: t });
    const detailRows = getDetailRows({ record, serviceIntervals, strings: t });
    const voidedClass = record.voided ? styles.isVoided : '';
    const photos = record.photos ?? [];
    const changeLog = record.changeLog ?? [];
    const showEditAction = getCanEditRecord({ isAdmin, record });

    const handleEdit = () => onEditRecord({ recordId: record.id });

    return (
        <VerticalContainer
            className={`${styles.serviceRecordDetail} ${voidedClass} ${className}`}
            testId={testId}>
            <Wrapper className={styles.backAction} onClick={onBack} testId={BACK_TEST_ID}>
                {t.SERVICE_RECORD_BACK}
            </Wrapper>

            <HorizontalContainer className={styles.summaryRow}>
                <Wrapper className={styles.summary}>{summary}</Wrapper>

                {showEditAction && (
                    <Wrapper
                        className={styles.editAction}
                        onClick={handleEdit}
                        testId={EDIT_TEST_ID}>
                        {t.SERVICE_RECORD_EDIT}
                    </Wrapper>
                )}
            </HorizontalContainer>

            {detailRows.map(detailRow => (
                <HorizontalContainer
                    className={styles.detailRow}
                    key={detailRow.label}
                    testId={DETAIL_ROW_TEST_ID}>
                    <Wrapper className={styles.detailLabel}>{detailRow.label}</Wrapper>

                    <Wrapper className={styles.detailValue}>{detailRow.value}</Wrapper>
                </HorizontalContainer>
            ))}

            {photos.length > 0 && (
                <HorizontalContainer className={styles.photos} testId={PHOTOS_TEST_ID}>
                    {photos.map(photo => (
                        <ServicePhotoThumb key={photo.id} photo={photo} />
                    ))}
                </HorizontalContainer>
            )}

            {changeLog.length > 0 && (
                <PanelSection count={changeLog.length} text={t.SERVICE_RECORD_CHANGE_LOG_HEADING}>
                    <ServiceRecordChangeLog changes={changeLog} />
                </PanelSection>
            )}

            <ServiceRecordAttribution record={record} />
        </VerticalContainer>
    );
};

export default ServiceRecordDetail;
